import React, { useEffect, useMemo, useState } from "react";
import orderBy from "lodash/orderBy";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import LinearProgress from "@mui/material/LinearProgress";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";

import {
  WAREHOUSE_NAMES,
  WAREHOUSE_LOCATIONS_COLLECTION,
  PROJECTS_DATA_QUERY,
} from "../../../../constants/graphql/queries";

const variables = {
  projectIds: [],
  search: "",
  excludeType: "base",
  locationType: "virtual_pallet",
  occupied: false,
  limit: 1,
  order: ["id", "asc"],
  active: true,
  verifyUnoccupied: true, // Defense against stale 'occupied' column - does live check
};

export default function SelectWarehouseModal({ open, onClose, onSelect, projectId }) {
  const { data: { warehouses = [] } = {}, loading } = useQuery(WAREHOUSE_NAMES, {
    fetchPolicy: "network-only",
    nextFetchPolicy: "cache-first",
    variables: { userWarehouses: true },
  });
  const {
    data: { projectsData } = {},
    loading: projectWarehousesLoading,
  } = useQuery(PROJECTS_DATA_QUERY, {
    variables: { projectIds: [projectId] },
    skip: !projectId,
  });
  const eligibleWarehouses = useMemo(() => {
    const projectWarehouses = projectsData?.warehouses;

    // Projects without configured warehouses retain the current all-warehouses behavior.
    if (!projectId || !projectWarehouses?.length) {
      return warehouses;
    }

    const projectWarehouseIds = new Set(projectWarehouses.map(warehouse => warehouse.id));
    return warehouses.filter(warehouse => projectWarehouseIds.has(warehouse.id));
  }, [projectId, projectsData, warehouses]);
  const list = orderBy(eligibleWarehouses, v => (v?.name || "").toLowerCase());
  const [selectedWarehouseId, setSelectedWarehouseId] = useState(null);
  const { loading: locationsLoading, data: locationsData } = useQuery(WAREHOUSE_LOCATIONS_COLLECTION, {
    variables: {
      warehouseId: selectedWarehouseId,
      ...variables,
    },
    skip: !selectedWarehouseId,
    fetchPolicy: "network-only",
  });

  useEffect(() => {
    if (locationsData) {
      const location = locationsData?.warehouseLocationsCollection?.collection?.[0];
      if (location) {
        const warehouse = eligibleWarehouses.find(w => w.id === selectedWarehouseId);
        onSelect({
          ...location,
          virtualPalletLocation: true,
          warehouse: warehouse,
          warehouseId: warehouse.warehouseId,
        });
        onClose();
      }
    }
  }, [locationsData]);

  useEffect(() => {
    if (!open) {
      setSelectedWarehouseId(null);
      return;
    }

    if (!loading && !projectWarehousesLoading && eligibleWarehouses.length === 1) {
      setSelectedWarehouseId(eligibleWarehouses[0].id);
    }
  }, [eligibleWarehouses, loading, open, projectWarehousesLoading]);

  const handleSelect = warehouse => {
    setSelectedWarehouseId(warehouse.id);
  };

  return (
    <Dialog
      open={open && !loading && !projectWarehousesLoading && eligibleWarehouses.length !== 1}
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle id="dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Box width={50} />
          <Typography variant="h2" color="textPrimary" align="center">
            Select a Warehouse
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {locationsLoading && (
          <Box height={0.5}>
            <LinearProgress />
          </Box>
        )}
        {loading || projectWarehousesLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="100%">
            <LinearProgress sx={{ width: "80%" }} />
          </Box>
        ) : (
          <Box height="100%" display="flex" justifyContent="center" alignItems="center" py={3}>
            <List sx={{ width: "100%" }}>
              {list.map(warehouse => (
                <React.Fragment key={warehouse.id}>
                  <Divider />
                  <ListItem disablePadding sx={{ p: 0 }}>
                    <ListItemButton onClick={() => handleSelect(warehouse)}>
                      <ListItemText sx={{ px: 2 }} primary={warehouse.name} />
                    </ListItemButton>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
